import {Action} from '@ngrx/store';
import * as _ from 'lodash';
import {currentClientActionTypes, LoadCurrentClientSuccessAction} from '../actions/current-client';
import {ChangeClientAction, LoadRolesSuccessAction, SelectItemAction, StartImpersonationSuccessAction, userContextActionTypes} from '../actions/user-context';
import {Client} from '../models/client';
import {ImpersonateUser} from '../models/impersonate-user';
import {Role} from '../models/role';
import {Site} from '../models/site';
import {currentSiteActionTypes, LoadCurrentSiteSuccessAction} from '../actions/current-site';
import {SubscriptionI} from '../models/subscription-i';
import {currentSubscriptionActionTypes, LoadCurrentSubscriptionSuccessAction} from '../actions/current-subscription';
import {Order} from '../../order/models/order';
import {currentOrderActionTypes, LoadCurrentOrderSuccessAction} from '../actions/current-order';

export interface UserContextState {
  client: Client;
  site: Site;
  order: Order;
  subscription: SubscriptionI;
  roles: Role[];
  impersonate: ImpersonateUser;
  selectedItem: string;
}

const initialState: UserContextState = {
  client: undefined,
  site: undefined,
  order: undefined,
  subscription: undefined,
  roles: [],
  impersonate: undefined,
  selectedItem: undefined
};

export function userContextReducer(state: UserContextState = initialState, action: Action): UserContextState {
  switch (action.type) {
    case userContextActionTypes.LOAD_ROLES_SUCCESS:
      return handleLoadRolesSuccessAction(state, action);
    case userContextActionTypes.LOAD_ROLES_FAIL:
      return handleLoadRolesFailAction(state);
    case userContextActionTypes.CHANGE_CLIENT:
      return handleChangeClientAction(state, action);
    case userContextActionTypes.START_IMPERSONATION_SUCCESS:
      return handleStartImpersonationSuccessAction(state, action);
    case userContextActionTypes.START_IMPERSONATION_FAIL:
    case userContextActionTypes.CLEAR_IMPERSONATION:
      return handleClearImpersonateAction(state);
    case currentClientActionTypes.LOAD_SUCCESS:
      return handleLoadCurrentClientDetailsAction(state, action);
    case currentOrderActionTypes.LOAD_SUCCESS:
      return handleLoadCurrentOrderAction(state, action);
    case currentSiteActionTypes.LOAD_SUCCESS:
      return handleLoadCurrentSiteDetailsAction(state, action);
    case currentSubscriptionActionTypes.LOAD_SUCCESS:
      return handleLoadCurrentSubscriptionDetailsAction(state, action);
    case userContextActionTypes.SELECT_ITEM:
      return handleLoadSelectedItemAction(state, action);
    case userContextActionTypes.CLEAR_SELECT_ITEM:
      return handleClearSelectedItemAction(state);
    default:
      return state;
  }
}

function handleLoadRolesSuccessAction(state: UserContextState, action: LoadRolesSuccessAction): UserContextState {
  return {
    client: state.client,
    site: state.site,
    order: state.order,
    subscription: state.subscription,
    roles: action.payload,
    impersonate: state.impersonate,
    selectedItem: state.selectedItem
  };
}

function handleLoadRolesFailAction(state: UserContextState): UserContextState {
  return {
    client: state.client,
    site: state.site,
    order: state.order,
    subscription: state.subscription,
    roles: [],
    impersonate: state.impersonate,
    selectedItem: state.selectedItem
  };
}

function handleChangeClientAction(state: UserContextState, action: ChangeClientAction): UserContextState {
  return {
    client: {uuid: action.payload.clientId},
    site: state.site,
    order: state.order,
    subscription: state.subscription,
    roles: state.roles,
    impersonate: state.impersonate,
    selectedItem: state.selectedItem
  };
}
function handleLoadCurrentClientDetailsAction(state: UserContextState, action: LoadCurrentClientSuccessAction): UserContextState {
  return {
    client: action.payload,
    site: null,
    subscription: null,
    order: state.order,
    roles: state.roles,
    impersonate: state.impersonate,
    selectedItem: state.selectedItem
  };
}

function handleLoadCurrentOrderAction(state: UserContextState, action: LoadCurrentOrderSuccessAction): UserContextState {
  return {
    client: state.client,
    site: null,
    subscription: null,
    order: action.payload,
    roles: state.roles,
    impersonate: state.impersonate,
    selectedItem: state.selectedItem
  };
}
function handleLoadCurrentSiteDetailsAction(state: UserContextState, action: LoadCurrentSiteSuccessAction): UserContextState {
  return {
    client: state.client,
    site: action.payload ? action.payload : null,
    subscription: null,
    order: null,
    roles: state.roles,
    impersonate: state.impersonate,
    selectedItem: state.selectedItem
  };
}

function handleLoadCurrentSubscriptionDetailsAction(state: UserContextState, action: LoadCurrentSubscriptionSuccessAction): UserContextState {
  return {
    client: state.client,
    site: state.site,
    subscription: action.payload ? action.payload : null,
    roles: state.roles,
    order: state.order,
    impersonate: state.impersonate,
    selectedItem: state.selectedItem
  };
}

function handleStartImpersonationSuccessAction(state: UserContextState, action: StartImpersonationSuccessAction): UserContextState {
  return {
    client: state.client,
    site: state.site,
    order: state.order,
    subscription: state.subscription,
    roles: state.roles,
    impersonate: action.payload,
    selectedItem: state.selectedItem
  };
}

function handleClearImpersonateAction(state: UserContextState): UserContextState {
  return {
    client: state.client,
    site: state.site,
    order: state.order,
    subscription: state.subscription,
    roles: state.roles,
    impersonate: undefined,
    selectedItem: state.selectedItem
  };
}

function handleLoadSelectedItemAction(state: UserContextState, action: SelectItemAction): UserContextState {
  return {
    client: state.client,
    site: state.site,
    order: state.order,
    subscription: state.subscription,
    roles: state.roles,
    impersonate: state.impersonate,
    selectedItem: action.payload
  };
}

function handleClearSelectedItemAction(state: UserContextState): UserContextState {
  return {
    client: state.client,
    site: state.site,
    order: state.order,
    subscription: state.subscription,
    roles: state.roles,
    impersonate: state.impersonate,
    selectedItem: undefined
  };
}

export const userContextSelectors = {
  client: (state: UserContextState) => _.get(state, 'client'),
  order: (state: UserContextState) => _.get(state, 'order'),
  site: (state: UserContextState) => _.get(state, 'site', null),
  subscription: (state: UserContextState) => _.get(state, 'subscription', null),
  roles: (state: UserContextState) => _.get(state, 'roles', []),
  impersonate: (state: UserContextState) => _.get(state, 'impersonate'),
  selectedItem: (state: UserContextState) => _.get(state, 'selectedItem', null)
};
